import { useState, useEffect } from "react";
import Tooltip from "./components/Tooltip.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, getFullscreenLayoutStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";

const MODULE_NAME = "_12_calendar_year";    

const CalendarYear = (props) => {
    const onMonthSelect = props.onMonthSelect || (() => { });
    const selectedMonth = props.selectedMonth || null;
    const selectedYear = props.selectedYear || new Date().getFullYear();
    const onToggleTrades = props.onToggleTrades || (() => { });

    // DECLARE ALL HOOKS FIRST - Before any conditional logic
    const [calendar, setCalendar] = useState({
        year: null,
        latest_month: null,
        monthly_profit: {},
        num_trades: {},
        tooltip: ""
    });
    const [allYears, setAllYears] = useState([]);
    const [currentYearIndex, setCurrentYearIndex] = useState(0);
    const [plMethod, setPlMethod] = useState(() => sessionStorage.getItem("plMethod") || "fifo");
    const [collapsed, setCollapsed] = useState(props.defaultCollapsed !== undefined ? props.defaultCollapsed : true);
    const [showTrades, setShowTrades] = useState(false);
    const [allTimeData, setAllTimeData] = useState({});
    const [includeOptionsProfit, setIncludeOptionsProfit] = useState(() => {
        const stored = sessionStorage.getItem("ytdpl_options_profit");
        return stored === "true";
    });             

    // Check if in fullscreen mode
    const isVisible = useModuleVisibility(MODULE_NAME);
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    // Set fullscreen indicator on mount/unmount
    useEffect(() => {
        if (isFullscreen) {
            document.documentElement.setAttribute('data-fullscreen', 'true');
            return () => document.documentElement.removeAttribute('data-fullscreen');
        }
    }, [isFullscreen]);

    // Fetch all available years
    useEffect(() => {
        fetch(
            `${process.env.REACT_APP_API_URL}/api/calendar_year/years`,
            { credentials: "include" }
        )
            .then(res => res.json())
            .then(data => {
                if (data && data.years && Array.isArray(data.years) && data.years.length > 0) {
                    const sortedYears = data.years.sort((a, b) => a - b);
                    setAllYears(sortedYears);
                    setCurrentYearIndex(sortedYears.length - 1);
                }
            })
            .catch(err => console.error("[CalendarYear] Failed to fetch years:", err));
    }, []);

    // Fetch calendar data for the current year
    useEffect(() => {
        if (allYears.length === 0) return;

        const year = allYears[currentYearIndex];

        fetch(
            `${process.env.REACT_APP_API_URL}/api/calendar_year?year=${year}&pl_method=${plMethod}`,
            { credentials: "include" }
        )
            .then(res => res.json())
            .then(data => {
                if (data && typeof data === "object" && data.year) {
                    setCalendar(data);
                }
            })
            .catch(err => console.error("[CalendarYear] Failed to fetch calendar:", err));
    }, [currentYearIndex, plMethod, allYears]);

    // Fetch all-time data for all years
    useEffect(() => {
        if (allYears.length === 0) return;

        const fetchAllTimeData = async () => {
            const allTimeMap = {};

            for (const year of allYears) {
                try {
                    const res = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/calendar_year?year=${year}&pl_method=${plMethod}`,
                        { credentials: "include" }
                    );
                    const data = await res.json();
                    if (data && data.monthly_profit) {
                        allTimeMap[year] = data.monthly_profit;
                    }
                } catch (err) {
                    console.error(`[CalendarYear] Failed to fetch calendar for year ${year}:`, err);
                }
            }

            setAllTimeData(allTimeMap);
        };

        fetchAllTimeData();
    }, [allYears, plMethod]);

    // Listen for plMethod changes
    useEffect(() => {
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            const handler = () => setPlMethod(sessionStorage.getItem("plMethod") || "fifo");
            window.addEventListener("plMethodChanged", handler);
            return () => window.removeEventListener("plMethodChanged", handler);
        }
    }, []);

    // Listen for ytdpl_options_profit changes
    useEffect(() => {
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            const handler = (event) => {
                const stored = sessionStorage.getItem("ytdpl_options_profit");
                setIncludeOptionsProfit(stored === "true");
            };
            window.addEventListener("optionsProfitChanged", handler);
            return () => window.removeEventListener("optionsProfitChanged", handler);
        }
    }, []);

    // Return null if component is not visible
    if (!isVisible) return null;

    const handleTradesToggle = () => {
        setShowTrades(!showTrades);
        onToggleTrades(!showTrades);
    };

    const handleMonthClick = (month) => {
        onMonthSelect(calendar.year || selectedYear, month);
    };

    const handlePriorYear = () => {
        if (currentYearIndex > 0) {
            setCurrentYearIndex(currentYearIndex - 1);
        }
    };

    const handleNextYear = () => {
        if (currentYearIndex < allYears.length - 1) {
            setCurrentYearIndex(currentYearIndex + 1);
        }
    };

    const getOptionsProfit = () => {
        if (!includeOptionsProfit) return 0;
        const stored = sessionStorage.getItem("ytdpl_options_profit_amount");
        return stored ? parseFloat(stored) : 0;
    };

    const getAllTimeOptionsProfit = () => {
        if (!includeOptionsProfit) return 0;
        const stored = sessionStorage.getItem("ytdpl_all_time_options_profit_amount");
        return stored ? parseFloat(stored) : 0;
    };

    const ytdStocksTotal = Object.values(calendar.monthly_profit || {}).reduce((sum, val) => sum + (val || 0), 0);
    const ytdOptionsProfit = getOptionsProfit();
    const ytdTotal = ytdStocksTotal + ytdOptionsProfit;

    const allTimeStocksTotal = Object.values(allTimeData).reduce((yearSum, monthlyData) => {
        return yearSum + Object.values(monthlyData || {}).reduce((sum, val) => sum + (val || 0), 0);
    }, 0);
    const allTimeOptionsProfit = getAllTimeOptionsProfit();
    const allTimeTotal = allTimeStocksTotal + allTimeOptionsProfit;

    const months = Array.from({ length: calendar.latest_month || 12 }, (_, i) => i + 1);
    const monthNames = Array.from({ length: 12 }, (_, i) =>
        new Date(2000, i, 1).toLocaleString("default", { month: "short" })
    );

    const rows = [];
    for (let i = 0; i < months.length; i += 4) {
        rows.push(months.slice(i, i + 4));
    }

    const canGoPrior = currentYearIndex > 0;
    const canGoNext = currentYearIndex < allYears.length - 1;

    const ytdColor = ytdTotal > 0 ? "green" : ytdTotal < 0 ? "red" : "#888";
    const allTimeColor = allTimeTotal > 0 ? "green" : allTimeTotal < 0 ? "red" : "#888";

    return (
        <div
            data-analytics={MODULE_NAME}
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`}
            style={getFullscreenContainerStyles(isFullscreen)}
        >
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <h4
                    className="collapse-toggle-headline"
                    onClick={() => setCollapsed(!collapsed)}
                    style={{ cursor: "pointer", userSelect: "none", marginTop: 0, marginBottom: collapsed ? 0 : 16, flex: 1 }}
                >
                    📆 Year-to-Date Profit Calendar ({calendar.year})
                    <Tooltip text={calendar.tooltip || "Year-to-date profit calendar. Click a month to view daily details."} />
                    <span
                        onClick={e => {
                            e.stopPropagation();
                            handleTradesToggle();
                        }}
                        title={showTrades ? "Hide trade counts" : "Show trade counts"}
                        style={{
                            marginLeft: 8,
                            cursor: "pointer",
                            color: showTrades ? "#1976d2" : "#3498db",
                            fontSize: 18,
                            verticalAlign: "middle",
                            userSelect: "none",
                            display: "inline-block",
                            transition: "color 0.2s ease"
                        }}
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleTradesToggle();
                            }
                        }}
                        role="button"
                        aria-pressed={showTrades}
                    >
                        Ⓣ
                    </span>
                </h4>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="Year-to-Date Calendar"
                        additionalParams={{ year: calendar.year }}
                    />
                )}
            </div>

            <div style={getFullscreenLayoutStyles(isFullscreen)}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        gap: isFullscreen ? 60 : 40,
                        marginTop: 16,
                        marginBottom: 0,
                        overflowX: isFullscreen ? "auto" : "visible",
                        paddingBottom: isFullscreen ? 16 : 0,
                        width: isFullscreen ? "100%" : "auto"
                    }}
                    className="year-navigation"
                >
                    <button
                        onClick={handlePriorYear}
                        disabled={!canGoPrior}
                        style={{
                            background: "none",
                            border: "none",
                            fontSize: 28,
                            cursor: canGoPrior ? "pointer" : "not-allowed",
                            color: canGoPrior ? "#4ECDC4" : "#555",
                            padding: "8px 12px",
                            transition: "all 0.2s ease",
                            opacity: canGoPrior ? 1 : 0.3,
                            minWidth: "40px",
                            textAlign: "center",
                            flexShrink: 0
                        }}
                        title={canGoPrior ? "Previous year" : "No prior years"}
                    >
                        ◀
                    </button>

                    <table
                        style={{
                            width: "auto",
                            borderCollapse: "collapse",
                            flexShrink: 0
                        }}
                    >
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={idx}>
                                    {row.map(m => {
                                        const val = calendar.monthly_profit?.[m];
                                        const trades = calendar.num_trades?.[m];
                                        const isSelected = selectedMonth === m && selectedYear === calendar.year;
                                        const color = (val == null || val === 0) ? "#888" : val > 0 ? "green" : "red";

                                        return (
                                            <td
                                                key={m}
                                                style={{
                                                    textAlign: "center",
                                                    padding: "10px",
                                                    background: isSelected ? "rgba(25, 118, 210, 0.1)" : "transparent",
                                                    borderRadius: "4px",
                                                    transition: "background 0.2s ease"
                                                }}
                                            >
                                                {val != null ? (
                                                    <div>
                                                        <button
                                                            style={{
                                                                color,
                                                                fontSize: 22,
                                                                padding: 8,
                                                                border: isSelected ? "2px solid #1976d2" : "none",
                                                                background: "none",
                                                                cursor: "pointer",
                                                                borderRadius: "4px",
                                                                transition: "all 0.2s ease",
                                                                fontWeight: isSelected ? "bold" : "normal"
                                                            }}
                                                            onClick={() => handleMonthClick(m)}
                                                            onMouseEnter={e => {
                                                                if (!isSelected) {
                                                                    e.target.style.background = "rgba(25, 118, 210, 0.05)";
                                                                }
                                                            }}
                                                            onMouseLeave={e => {
                                                                if (!isSelected) {
                                                                    e.target.style.background = "none";
                                                                }
                                                            }}
                                                            title={`View ${monthNames[m - 1]} details`}
                                                        >
                                                            {monthNames[m - 1]}
                                                        </button>
                                                        <br />
                                                        <span
                                                            style={{
                                                                color,
                                                                fontSize: 12,
                                                                fontWeight: isSelected ? "600" : "normal",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            ${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                        </span>
                                                        {showTrades && (
                                                            <div
                                                                style={{
                                                                    color,
                                                                    fontSize: 12,
                                                                    marginTop: 4,
                                                                    opacity: 0.8,
                                                                    whiteSpace: "nowrap"
                                                                }}
                                                            >
                                                                Trades: {trades != null ? trades : 0}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <span style={{ fontSize: 22, color: "#888" }}>
                                                            {monthNames[m - 1]}
                                                        </span>
                                                        <br />
                                                        <span style={{ fontSize: 12, color: "#888" }}>
                                                            —
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 24,
                            paddingTop: 8,
                            flexShrink: 0,
                            minWidth: "180px"
                        }}
                    >
                        <div>
                            <h3
                                style={{
                                    margin: "0 0 8px 0",
                                    color: "#aaa",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5
                            }}
                            >
                                YTD Total
                            </h3>
                            <h3
                                style={{
                                    margin: 0,
                                    color: ytdColor,
                                    fontSize: 32,
                                    fontWeight: 700,
                                    whiteSpace: "nowrap"
                            }}
                            >
                                ${ytdTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </h3>
                            {includeOptionsProfit && ytdOptionsProfit !== 0 && (
                                <div
                                    style={{
                                        marginTop: 6,
                                        fontSize: 12,
                                        color: "#999",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    (Stocks: ${ytdStocksTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}, Options: ${ytdOptionsProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })})
                                </div>
                            )}
                        </div>
                        <div>
                            <h3
                                style={{
                                    margin: "0 0 8px 0",
                                    color: "#aaa",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5
                            }}
                            >
                                All Time
                            </h3>
                            <h3
                                style={{
                                    margin: 0,
                                    color: allTimeColor,
                                    fontSize: 32,
                                    fontWeight: 700,
                                    whiteSpace: "nowrap"
                            }}
                            >
                                ${allTimeTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </h3>
                            {includeOptionsProfit && allTimeOptionsProfit !== 0 && (
                                <div
                                    style={{
                                        marginTop: 6,
                                        fontSize: 12,
                                        color: "#999",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    (Stocks: ${allTimeStocksTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}, Options: ${allTimeOptionsProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })})
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={handleNextYear}
                        disabled={!canGoNext}
                        style={{
                            background: "none",
                            border: "none",
                            fontSize: 28,
                            cursor: canGoNext ? "pointer" : "not-allowed",
                            color: canGoNext ? "#4ECDC4" : "#555",
                            padding: "8px 12px",
                            transition: "all 0.2s ease",
                            opacity: canGoNext ? 1 : 0.3,
                            minWidth: "40px",
                            textAlign: "center",
                            flexShrink: 0
                        }}
                        title={canGoNext ? "Next year" : "No more years"}
                    >
                        ▶
                    </button>
                </div>

                <div className="collapse-content">
                    {/* Empty collapse-content to satisfy CSS */}
                </div>
            </div>
        </div>
    );
};

export default CalendarYear;